import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import NoResults from 'src/components/NoResults';
import { useSnackbar } from 'notistack';
import { createAxiosInstance } from 'src/utils/axios';
import CircularLoader from 'src/components/CircularLoader';
import { POLICY_ROUTER_ECO_SERVICES } from './constants/policyRouterServices';
import PolicyRouterService from 'src/services/PolicyRouterService';
import LogDetailsModal from './modals/LogDetailsModal';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
  Box,
} from "@material-ui/core";
import BallotIcon from '@material-ui/icons/Ballot';
import SearchIcon from '@material-ui/icons/Search';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';

//Logs will need to be rulled up by transaction ID, as each transaction can have multiple entries (one for each service it touches - policy router, wrapper, policy lookup). 
// The transaction ID is the common thread that will allow us to tie all of those together in the UI.

const searchTypes = [
  { value: "dateRange", label: "Date Range" },
  { value: "policy", label: "Policy Number" },
  { value: "vin", label: "VIN" },
  { value: "transaction", label: "Transaction ID" },
];

/**
 * Groups flat client records by clientName into a deduplicated,
 * alphabetically sorted array. Each group contains the shared
 * clientName and an array of associated { clientNumber, clientAbbreviation } records.
 *
 * @param {Array<{ clientName?: string, clientNumber: string|number, clientAbbreviation?: string, isActive?: boolean }>} clients
 * @returns {Array<{ clientName: string, clients: Array<{ clientNumber: string|number, clientAbbreviation: string }> }>}
 */
/** Validates a standard UUID v4 format (8-4-4-4-12 hex). */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidUUID(value) {
  return UUID_REGEX.test(value.trim());
}

/**
 * VIN characters: digits 0-9 and uppercase letters EXCEPT I, O, Q.
 * Accepts a partial VIN (last 8+) up to a full 17-character VIN.
 */
const VIN_CHAR_REGEX = /^[A-HJ-NPR-Z0-9]+$/i;
const VIN_MIN_LENGTH = 8;
const VIN_MAX_LENGTH = 17;
function isValidVIN(value) {
  const v = value.trim();
  // Accept last 8 characters OR full 17-character VIN
  if (v.length !== VIN_MIN_LENGTH && v.length !== VIN_MAX_LENGTH) return false;
  return VIN_CHAR_REGEX.test(v);
}

function groupClientsByName(clients) {
  const grouped = {};

  clients.forEach((c) => {
    const name = c.clientName?.trim();
    if (!name || !c.isActive) return;

    if (!grouped[name]) {
      grouped[name] = { clientName: name, clients: [] };
    }

    grouped[name].clients.push({
      clientNumber: c.clientNumber,
      clientAbbreviation: c.clientAbbreviation?.trim() || '',
    });
  });

  return Object.values(grouped).sort((a, b) =>
    a.clientName.localeCompare(b.clientName)
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
  },
  contentBody: {
    marginTop: '-5px',
    padding: theme.spacing(2),
    height: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(3),
    },
  },
  noResultsContainer: {
    marginTop: '20%',
  },
  loadingContainer: {
    marginTop: '30%',
  },
  pageTitle: {
    color: theme.palette.secondary.main,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  searchCard: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
    margin: '0',
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  resultsCard: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    overflow: 'auto',
  },
  searchTypeLabel: {
    marginBottom: theme.spacing(0.5),
    display: 'block',
  },
  searchTypeGrid: {
    marginTop: theme.spacing(0.5),
  },
  searchValueGrid: {
    marginTop: theme.spacing(0.5),
  },
  tableRowClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  logSearchDialogHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  logSearchDialogHeaderTypography: {
    fontSize: '20px',
  },
  sidebar: {
    width: 240,
    float: 'left',
    borderRight: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.grey[900]
      : theme.palette.primary.main,
    color: theme.palette.mode === 'dark'
      ? theme.palette.text.primary
      : theme.palette.common.white,
  },
  contentArea: {
    flex: 1,
    float: 'left',
    padding: theme.spacing(3),
    width: '70%',
    overflow: 'auto',
  },
  metadata: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    marginBottom: theme.spacing(3),
  },
  logDetailsDialog: {
    minHeight: 600,
    [theme.breakpoints.up('lg')]: {
      minHeight: 750,
    },
  },
  toggleButtonGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  toggleButton: {
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(0.75, 2),
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: { xs: 'space-between', md: 'flex-end' },
    gap: theme.spacing(6),
    flexWrap: 'wrap',
  },
  searchButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: { md: 140 },
  },
  resetButton: {
    width: { xs: '48%', md: 'auto' },
  },
}));

function PolicyRouterLogSearchDetail({ 
  title,
  applicationId,
  onSave,
}) {

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { email } = useSelector((state) => state.account.user);
  const policyRouterService = useMemo(
    () => new PolicyRouterService(
      createAxiosInstance(process.env.REACT_APP_POLICY_ROUTER_CONFIG_API),
      applicationId
    ),
    [applicationId]
  );
  const todaysDate = new Date().toISOString().split("T")[0];

  const [selectedClientName, setSelectedClientName] = useState("");
  const [selectedClientNumber, setSelectedClientNumber] = useState("");
  const [searchType, setSearchType] = useState("dateRange");
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(todaysDate);
  const [results, setResults] = useState(null);
  const [selectedLog, setselectedLog] = useState(null);

  const [groupedClients, setGroupedClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSubmittedSearch, setHasSubmittedSearch] = useState(false);
  const [activeService, setActiveService] = useState(POLICY_ROUTER_ECO_SERVICES.POLICY_ROUTER);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);


  // Derived: find the group for the currently selected client name
  const selectedClientGroup = groupedClients.find(
    (g) => g.clientName === selectedClientName
  );
  const hasMultipleRecords = selectedClientGroup?.clients?.length > 1;

  const handleRowClick = (row) => {
    setselectedLog(row);
    console.log("Selected log details:", row);
  };

  const handleCloseModal = () => {
    setselectedLog(null);
  };

  const handleSearch = async () => {
    setHasSubmittedSearch(true);

    // Trim leading/trailing whitespace before sending
    const trimmedValue = searchValue.trim();
    setSearchValue(trimmedValue);

    if (hasValidationErrors) {
      return;
    }

    // --- Pre-validation ---
    if (!email) {
      enqueueSnackbar('User login email is required to perform search.', { variant: 'error' });
      return;
    }

    // --- Build payload ---
    const payload = {
      user: email || 'nsd-fe-user', // Fallback in case email is not available
      transactionId: searchType === 'transaction' ? trimmedValue : null,
      clientNumber: selectedClientNumber || null,
      policyNumber: searchType === 'policy' ? trimmedValue : null,
      vin: searchType === 'vin' ? trimmedValue : null,
      startDate: startDate || null,
      endDate: endDate || null,
    };

    setPage(0);
    setIsSearching(true);
    try {
      const response = await policyRouterService.searchPolicyRouterLogsBasic(payload);
      const data = response.data;

      // Roll up individual log entries by transactionId
      const merged = data?.results
        ? policyRouterService.mergeTransactionLogs(data)
        : [];

      setResults(merged);
    } catch (error) {
      enqueueSnackbar(`Error searching logs. ${error.message || ''}`, { variant: 'error' });
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSelectedClientName("");
    setSelectedClientNumber("");
    setSearchType("dateRange");
    setSearchValue("");
    setStartDate("");
    setEndDate("");
    setHasSubmittedSearch(false);
  };

  const handleClientNameChange = (e) => {
    const name = e.target.value;
    setSelectedClientName(name);

    const group = groupedClients.find((g) => g.clientName === name);
    if (group && group.clients.length === 1) {
      // Auto-select when only one record exists
      setSelectedClientNumber(group.clients[0].clientNumber);
    } else {
      setSelectedClientNumber("");
    }
  };

  const handleSearchTypeChange = (_event, newValue) => {
    // ToggleButtonGroup passes null when the same button is clicked;
    // keep the current selection to enforce "always one selected".
    if (newValue !== null) {
      setSearchType(newValue);
      setHasSubmittedSearch(false);
    }
  };

  const trimmedSearch = searchValue.trim();
  const selectedClientNumberValue = selectedClientNumber?.toString().trim() || '';
  const requiresSearchValue = searchType !== 'dateRange';
  const requiresDateRange = searchType !== 'transaction';
  const requiresClientSelection = searchType === 'dateRange';
  const clientSelectionError = requiresClientSelection && !selectedClientNumberValue
    ? 'Client selection is required'
    : '';
  const dateStartError = requiresDateRange && !startDate
    ? 'Start Date is required'
    : '';
  const dateEndError = requiresDateRange && !endDate
    ? 'End Date is required'
    : '';
  const searchValueError = (() => {
    if (!trimmedSearch) {
      return requiresSearchValue ? 'Search value is required' : '';
    }
    if (searchType === 'transaction' && !isValidUUID(trimmedSearch)) {
      return 'Enter a valid UUID (e.g. 605d459b-b484-4178-8c85-0ae004539612)';
    }
    if (searchType === 'vin') {
      if (!VIN_CHAR_REGEX.test(trimmedSearch)) {
        return 'VIN can only contain digits and letters (excluding I, O, Q)';
      }
      if (trimmedSearch.length < VIN_MIN_LENGTH) {
        return `Enter at least the last ${VIN_MIN_LENGTH} characters of the VIN`;
      }
      if (trimmedSearch.length > VIN_MAX_LENGTH) {
        return `VIN cannot exceed ${VIN_MAX_LENGTH} characters`;
      }
    }
    return '';
  })();
  const hasValidationErrors = Boolean(
    clientSelectionError ||
    dateStartError ||
    dateEndError ||
    searchValueError
  );

  const isSearchDisabled = isSearching;

  function formatTimestampToTime(timestamp, timeZone = "UTC") {
    const date = new Date(timestamp);

    const zone =
      timeZone === "UTC" ? "UTC" : "America/New_York"; // handles EST/EDT automatically

    return new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: zone,
    }).format(date);
  };


  useEffect(() => {
    (async () => {
        setIsLoading(false);
      await Promise.all([
        getPolicyRouterClientNames()
      ]);
    })();
  }, []);

  async function getPolicyRouterClientNames() {
    try {
      setIsLoading(true);
      setIsError(false);

      const policyRouterClientNames = await policyRouterService.getPolicyRouterClientNames();
      const data = policyRouterClientNames.data;

      // Filter active clients and sort alphabetically by client name that arn't empty, 
      // as those are the only ones that should be returned in the dropdown. 
      // The full list of clients with empty client numbers is still needed for other areas of the application, 
      // but not for this dropdown.
      const grouped = groupClientsByName(data);
      setGroupedClients(grouped);
    } catch (error) {
      enqueueSnackbar('Error fetching clients list', { variant: 'error' });
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }

  function isNullOrEmpty(str) {
    return !str || str.trim() === '';
  }

  if (isError) {
    return (
      <div className={classes.noResultsContainer} title="Error loading details">
        <NoResults />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularLoader />
      </div>
    );
  }

  return (
    <main className={classes.root}>
      <div className={classes.contentBody}>
        <Grid item xs={12} md={12}>
          <Typography variant="h3" className={classes.pageTitle}>
            {isNullOrEmpty(title)
              ? 'Policy Router Log Search'
              : title}
          </Typography>
          <Typography variant="h6" className={classes.pageTitle}>
            Search and view logs for Policy Router activity.
          </Typography>
        </Grid>

        <Grid item xs={12} md={12}>
          <Card className={classes.searchCard}>
            <CardHeader 
              title="Search Criteria" 
              avatar={<SearchIcon color="primary" />} 
            />
            <CardContent>
              <Grid container spacing={3}>
                {/* Primary Client Dropdown — unique names only */}
                <Grid item xs={12} md={hasMultipleRecords ? 3 : 4}>
                  <TextField
                    select
                    label="Client"
                    fullWidth
                    value={selectedClientName}
                    onChange={handleClientNameChange}
                    error={hasSubmittedSearch && Boolean(clientSelectionError)}
                    helperText={hasSubmittedSearch ? clientSelectionError : ''}
                  >
                    {groupedClients.map((g) => (
                      <MenuItem key={g.clientName} value={g.clientName}>
                        {g.clientName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Secondary Dropdown — only when multiple records share the same name */}
                {hasMultipleRecords && (
                  <Grid item xs={12} md={2}>
                    <TextField
                      select
                      label="Client Number"
                      fullWidth
                      value={selectedClientNumber}
                      onChange={(e) => setSelectedClientNumber(e.target.value)}
                    >
                      {selectedClientGroup.clients.map((c) => (
                        <MenuItem key={c.clientNumber} value={c.clientNumber}>
                          {c.clientNumber}{c.clientAbbreviation ? ` (${c.clientAbbreviation})` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}              
              </Grid>

              {/* Search Type Toggle Buttons */}
              <Grid container spacing={3} className={classes.searchTypeGrid}>
                <Grid item xs={12}>
                  <Typography 
                    variant="caption" 
                    color="textSecondary" 
                    className={classes.searchTypeLabel}
                  >
                    Search Type
                  </Typography>
                  <ToggleButtonGroup
                    value={searchType}
                    exclusive
                    onChange={handleSearchTypeChange}
                    aria-label="Search type selection"
                    className={classes.toggleButtonGroup}
                  >
                    {searchTypes.map((t) => (
                      <ToggleButton
                        key={t.value}
                        value={t.value}
                        aria-label={t.label}
                        className={classes.toggleButton}
                      >
                        {t.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>
              </Grid>

              {/* Date Range Fields */}
              <Grid container spacing={3}>
                {!(searchType === 'transaction') && (
                  <>
                    <Grid item xs={12} md={3}>
                      <TextField
                        type="date"
                        label="Start Date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        value={startDate}
                        inputProps={{
                          min: '2025-01-01',
                          max: todaysDate,
                        }}
                        onChange={(e) => setStartDate(e.target.value)}
                        error={hasSubmittedSearch && Boolean(dateStartError)}
                        helperText={hasSubmittedSearch ? dateStartError : ''}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <TextField
                        type="date"
                        label="End Date"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          max: todaysDate,
                        }}
                        fullWidth
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        error={hasSubmittedSearch && Boolean(dateEndError)}
                        helperText={hasSubmittedSearch ? dateEndError : ''}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Search Value Field */}
              <Grid className={classes.searchValueGrid}>
                <Grid container spacing={3}>
                  {!(searchType === 'dateRange') && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={`Search ${searchType}`}
                        fullWidth
                        value={searchValue}
                        disabled={searchType === 'dateRange'}
                        onChange={(e) => setSearchValue(e.target.value)}
                        error={hasSubmittedSearch && Boolean(searchValueError)}
                        helperText={hasSubmittedSearch ? searchValueError : ''}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={7} />
                <Grid item xs={12} md={5}>
                  <Box className={classes.buttonContainer}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isSearching || isSearchDisabled}
                      onClick={handleSearch}
                      fullWidth={{ xs: true, md: false }}
                      sx={classes.searchButton}
                      startIcon={
                        isSearching ? (
                          <CircularLoader size={15} sx={{ color: 'white' }} />
                        ) : (
                          <SearchIcon size={15} sx={{ color: 'white' }} />
                        )
                      }
                    >
                      {isSearching ? 'Searching...' : 'Search'}
                    </Button>

                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleClearSearch}
                      fullWidth={{ xs: true, md: false }}
                      sx={classes.resetButton}
                    >
                      Reset
                    </Button>
                  </Box>
                </Grid>
              </Grid>

            </CardContent>
          </Card>
        </Grid>

        {/* Results Section */}
        {results && (
          <Card className={classes.resultsCard}>
            <CardHeader 
              title="Results" 
              avatar={<BallotIcon color="primary" />} 
            />
            <CardContent>
              {results?.length === 0 && (
                <Typography variant="body1">
                  No results found. Please adjust your search criteria and try again.
                </Typography>
              )}
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Time</strong></TableCell>
                    <TableCell><strong>Client Name</strong></TableCell>
                    <TableCell><strong>Transaction ID</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        className={classes.tableRowClickable}
                        onClick={() => handleRowClick(row)}
                      >
                        <TableCell>{row.datestamp}</TableCell>
                        <TableCell>{formatTimestampToTime(row.timestamp)}</TableCell>
                        <TableCell>
                          {row.clientName} 
                          {row.clientAbbreviation && `(${row.clientAbbreviation})`}
                        </TableCell>
                        <TableCell>{row.transactionId}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      count={results?.length || 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onChangePage={(event, newPage) => setPage(newPage)}
                      onChangeRowsPerPage={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                      }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </CardContent>
          </Card>
        )}

        <LogDetailsModal
          open={Boolean(selectedLog)}
          selectedLog={selectedLog}
          activeService={activeService}
          setActiveService={setActiveService}
          onClose={handleCloseModal}
          classes={classes}
        />

      </div>
    </main>
  );
}

PolicyRouterLogSearchDetail.propTypes = {
  title: PropTypes.string,
  applicationId: PropTypes.string.isRequired,
  onSave: PropTypes.func,
};

PolicyRouterLogSearchDetail.defaultProps = {
  //isReadOnly: false,
};

export default PolicyRouterLogSearchDetail;
